import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const EXTERNAL_CART_COUNT_EVENT = 'baseform:cart-count-sync';
const CART_MUTATION_SUFFIXES = ['/cart/change', '/cart/update', '/cart/clear'];
const CART_ADD_SUFFIX = '/cart/add';

/**
 * Normalize a storefront URL so both `/cart/change` and `/cart/change.js`
 * (including locale-prefixed routes) match the same cart mutation.
 *
 * @param {string | URL | Request | undefined} input
 * @returns {string}
 */
function cartRequestPath(input) {
  try {
    const rawUrl =
      typeof input === 'string' || input instanceof URL
        ? input.toString()
        : input instanceof Request
          ? input.url
          : '';

    if (!rawUrl) return '';

    return new URL(rawUrl, window.location.origin).pathname.replace(/\.js$/, '').replace(/\/+$/, '');
  } catch (_) {
    return '';
  }
}

/**
 * @param {string | URL | Request | undefined} input
 * @returns {boolean}
 */
function isCartMutationRequest(input) {
  const path = cartRequestPath(input);
  return CART_MUTATION_SUFFIXES.some((suffix) => path === suffix || path.endsWith(suffix));
}

/**
 * @param {string | URL | Request | undefined} input
 * @returns {boolean}
 */
function isCartAddRequest(input) {
  const path = cartRequestPath(input);
  return path === CART_ADD_SUFFIX || path.endsWith(CART_ADD_SUFFIX);
}

/**
 * @param {unknown} payload
 * @returns {number | null}
 */
function cartCountFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;

  const directCount = Number(payload.item_count ?? payload.itemCount ?? payload.totalQuantity);
  if (Number.isFinite(directCount) && directCount >= 0) return directCount;

  if (!Array.isArray(payload.items)) return null;

  const quantity = payload.items.reduce((sum, item) => {
    const value = Number(item?.quantity);
    return sum + (Number.isFinite(value) && value > 0 ? value : 0);
  }, 0);

  return quantity;
}

/**
 * Publish an authoritative cart count to every Horizon cart icon on the page.
 *
 * @param {number} itemCount
 */
function publishExternalCartCount(itemCount) {
  if (!Number.isFinite(itemCount) || itemCount < 0) return;

  document.dispatchEvent(
    new CustomEvent(EXTERNAL_CART_COUNT_EVENT, {
      detail: { itemCount },
    })
  );
}

let canonicalCartSyncRequestId = 0;

/**
 * Read Shopify's full Ajax cart after third-party mutations whose response does
 * not represent the complete cart (notably /cart/add.js). Every call gets its
 * own request ID so a slower, older response can never overwrite a newer count.
 *
 * @param {typeof window.fetch} fetcher
 * @returns {Promise<void>}
 */
async function syncCanonicalCartCount(fetcher) {
  const requestId = ++canonicalCartSyncRequestId;
  const cartUrl =
    typeof Theme !== 'undefined' && Theme.routes?.cart_url ? `${Theme.routes.cart_url}.js` : '/cart.js';

  try {
    const response = await fetcher(cartUrl, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!response.ok) return;

    const payload = await response.json();

    // Ignore an older cart snapshot if another sync started after it.
    if (requestId !== canonicalCartSyncRequestId) return;

    const itemCount = cartCountFromPayload(payload);
    if (itemCount !== null) publishExternalCartCount(itemCount);
  } catch (_) {
    // Cart syncing must never interrupt the storefront or a third-party cart.
  }
}

/**
 * Attravo updates Shopify's Ajax cart directly and doesn't emit Horizon's
 * StandardEvents.cartLinesUpdate event. Bridge add/change/update/clear requests
 * back into Horizon so the blue header Cart count cannot become stale.
 *
 * Shopify's /cart/add.js response can contain only the newly added line(s), not
 * the entire cart, so successful adds always re-read /cart.js before publishing
 * a count. change/update/clear responses can still be used directly when they
 * contain an authoritative cart total.
 */
if (!window.__baseformExternalCartFetchSync) {
  const previousFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const response = await previousFetch(...args);

    if (response.ok && isCartAddRequest(args[0])) {
      syncCanonicalCartCount(previousFetch);
    } else if (response.ok && isCartMutationRequest(args[0])) {
      response
        .clone()
        .json()
        .then((payload) => {
          const itemCount = cartCountFromPayload(payload);

          if (itemCount !== null) {
            publishExternalCartCount(itemCount);
          } else {
            syncCanonicalCartCount(previousFetch);
          }
        })
        .catch(() => syncCanonicalCartCount(previousFetch));
    }

    return response;
  };

  Object.defineProperty(window, '__baseformExternalCartFetchSync', {
    value: true,
    configurable: false,
  });
}

/**
 * Some cart apps still use XMLHttpRequest instead of fetch. Mirror the same
 * add/change/update/clear bridge for those requests.
 */
if (!window.__baseformExternalCartXhrSync) {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const xhrCartUrl = Symbol('baseformCartUrl');

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this[xhrCartUrl] = url;
    return originalOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    const isAdd = isCartAddRequest(this[xhrCartUrl]);
    const isMutation = isCartMutationRequest(this[xhrCartUrl]);

    if (isAdd || isMutation) {
      this.addEventListener(
        'loadend',
        () => {
          if (this.status < 200 || this.status >= 300) return;

          if (isAdd) {
            syncCanonicalCartCount(window.fetch.bind(window));
            return;
          }

          try {
            const payload =
              this.responseType === 'json' ? this.response : JSON.parse(this.responseText || 'null');
            const itemCount = cartCountFromPayload(payload);

            if (itemCount !== null) {
              publishExternalCartCount(itemCount);
              return;
            }
          } catch (_) {
            // Fall back to Shopify's canonical cart JSON below.
          }

          syncCanonicalCartCount(window.fetch.bind(window));
        },
        { once: true }
      );
    }

    return originalSend.apply(this, args);
  };

  Object.defineProperty(window, '__baseformExternalCartXhrSync', {
    value: true,
    configurable: false,
  });
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.addEventListener(EXTERNAL_CART_COUNT_EVENT, this.onExternalCartCount);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.removeEventListener(EXTERNAL_CART_COUNT_EVENT, this.onExternalCartCount);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles cart mutations made by third-party drawers such as Attravo.
   * @param {CustomEvent<{ itemCount: number }>} event
   */
  onExternalCartCount = (event) => {
    const itemCount = Number(event.detail?.itemCount);
    if (!Number.isFinite(itemCount) || itemCount < 0) return;

    this.renderCartBubble(itemCount, false);
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
